import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from '../firebase';
import { signInWithPopup, GoogleAuthProvider, signInWithEmailAndPassword } from 'firebase/auth';
import { useAuth } from '../AuthContext';
import { toast } from 'react-toastify';
import { Loader2, Leaf } from 'lucide-react';
import PlantImage from '../components/fancyplants/PlantImage';

function LoginPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const signInWithGoogle = () => {
    setLoading(true);
    const provider = new GoogleAuthProvider();
    signInWithPopup(auth, provider)
      .then(() => {
        toast.success('Signed in successfully!');
        navigate('/account');
      })
      .catch((error) => {
        console.error(error);
        toast.error('Failed to sign in. Please try again.');
      })
      .finally(() => setLoading(false));
  };

  const signInWithEmail = (e) => {
    e.preventDefault();
    setLoading(true);
    signInWithEmailAndPassword(auth, email, password)
      .then(() => {
        toast.success('Signed in successfully!');
        navigate('/account');
      })
      .catch((error) => {
        console.error(error);
        toast.error('Failed to sign in. Please check your email and password.');
      })
      .finally(() => setLoading(false));
  };

  const signOut = () => {
    setLoading(true);
    auth.signOut()
      .then(() => {
        toast.success('Signed out successfully');
        navigate('/');
      })
      .catch((error) => {
        console.error(error);
        toast.error('Failed to sign out. Please try again.');
      })
      .finally(() => setLoading(false));
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas">
        <Loader2 className="h-12 w-12 animate-spin text-forest-700" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas pt-16">
      <div className="container-fp grid items-stretch gap-0 py-10 lg:grid-cols-2">
        <div className="relative hidden overflow-hidden rounded-l-3xl bg-forest-900 lg:block">
          <PlantImage
            src="https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?auto=format&fit=crop&w=900&q=70"
            alt="Exotic foliage in a glasshouse"
            ratio="auto"
            className="absolute inset-0 h-full w-full opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-green-900 to-green-900/40" />
          <div className="relative flex h-full flex-col justify-end p-10 text-canvas">
            <div className="mb-3 flex items-baseline gap-1.5">
              <span className="font-display text-3xl">Fancy</span>
              <span className="font-display text-3xl italic text-brass">Plants</span>
            </div>
            <p className="max-w-xs text-canvas/80">
              Sign in to track your orders, manage your wishlist and leave verified reviews once
              your plants arrive.
            </p>
          </div>
        </div>

        <div className="surface rounded-3xl p-8 sm:p-10 lg:rounded-l-none lg:border-l-0">
          {user ? (
            <div>
              <h1 className="t-h2 mb-2">Welcome back{user.displayName ? `, ${user.displayName}` : ''}.</h1>
              <p className="mb-6 text-ink-soft">{user.email}</p>
              <Link to="/account" className="btn-forest mb-3 w-full">Go to your account</Link>
              <button onClick={signOut} className="btn-outline w-full" disabled={loading}>
                Sign out
              </button>
            </div>
          ) : (
            <div>
              <p className="eyebrow mb-2">Welcome back</p>
              <h1 className="t-h2 mb-6">Sign in</h1>
              <form onSubmit={signInWithEmail} className="space-y-4">
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium">Email</span>
                  <input
                    className="fp-input"
                    type="email"
                    placeholder="you@email.ie"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium">Password</span>
                  <input
                    className="fp-input"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </label>
                <button className="btn-brass w-full" type="submit" disabled={loading}>
                  Sign in
                </button>
              </form>

              <div className="my-6 flex items-center gap-3 text-xs text-ink-soft">
                <span className="h-px flex-1 bg-line" /> or <span className="h-px flex-1 bg-line" />
              </div>

              <button onClick={signInWithGoogle} className="btn-outline w-full" disabled={loading}>
                <svg className="h-5 w-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                Sign in with Google
              </button>

              <p className="mt-6 text-center text-sm text-ink-soft">
                New here?{' '}
                <Link to="/register" className="link-underline font-semibold">Create an account</Link>
              </p>
              <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-ink-soft">
                <Leaf size={13} className="text-forest-500" /> No account needed to shop — checkout as a guest anytime.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
